import { useEffect, useState } from "react";
import { Alert, Badge, Button, Form, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faSpinner,
  faTrash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {
  formatDateInput,
  formatTimeInput,
  normalizeTimeValue,
} from "../utils/helpers";

function getInitialForm(row) {
  const note = row?.worktime_note;

  return {
    date: formatDateInput(note?.date || row?.date),
    start_time: formatTimeInput(note?.start_time || row?.start_time),
    end_time: formatTimeInput(note?.end_time || row?.end_time),
    note: note?.note || "",
  };
}

function validate(form) {
  const errors = {};

  if (!form.date) errors.date = "Data jest wymagana.";
  if (!form.start_time) errors.start_time = "Godzina rozpoczęcia jest wymagana.";
  if (!form.end_time) errors.end_time = "Godzina zakończenia jest wymagana.";
  if (!form.note.trim()) errors.note = "Opis jest wymagany.";

  return errors;
}

// Porównuje zapisaną notatkę z tym, co jest w formularzu — bez zmian nie ma po co
// strzelać do serwera.
function isNoteChanged(note, payload) {
  return (
    formatDateInput(note.date) !== payload.date ||
    formatTimeInput(note.start_time) !== payload.start_time ||
    formatTimeInput(note.end_time) !== payload.end_time ||
    (note.note || "").trim() !== payload.note
  );
}

export default function WorkTimeNoteModal({
  show,
  row,
  canManageNote,
  canApproveNote,
  onHide,
  onSaveNote,
  onApproveNote,
  onDeleteNote,
}) {
  const [form, setForm] = useState(getInitialForm(row));
  const [errors, setErrors] = useState({});
  const [action, setAction] = useState("");
  const [error, setError] = useState("");

  const note = row?.worktime_note || null;
  const hasNote = !!note;
  const approved = !!note?.approved;
  const loading = !!action;
  const readOnly = !canManageNote || approved;

  useEffect(() => {
    if (show) {
      setForm(getInitialForm(row));
      setErrors({});
      setAction("");
      setError("");
    }
  }, [show, row]);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const runAction = async (name, callback) => {
    if (loading) return;

    setAction(name);
    setError("");

    const result = await callback();

    setAction("");

    if (!result?.success) {
      setError(result?.error || "Operacja nie powiodła się.");
      return;
    }

    onHide?.();
  };

  const handleSave = () => {
    const nextErrors = validate(form);

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    const payload = {
      date: form.date,
      start_time: normalizeTimeValue(form.start_time),
      end_time: normalizeTimeValue(form.end_time),
      note: form.note.trim(),
    };

    if (hasNote && !isNoteChanged(note, payload)) {
      onHide?.();
      return;
    }

    runAction("save", () => onSaveNote?.(row.id, note?.id, payload));
  };

  const handleApprove = (value) =>
    runAction(value ? "approve" : "reject", () =>
      onApproveNote?.(row.id, note.id, value),
    );

  const handleDelete = () =>
    runAction("delete", () => onDeleteNote?.(row.id, note.id));

  return (
    <Modal show={show} onHide={loading ? undefined : onHide} centered>
      <Modal.Header closeButton={!loading}>
        <Modal.Title>Notatka pracownika</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="mb-3">
          {hasNote ? (
            <Badge bg={approved ? "success" : "warning"}>
              {approved ? "Zatwierdzona" : "Oczekuje na zatwierdzenie"}
            </Badge>
          ) : (
            <Badge bg="secondary">Brak notatki</Badge>
          )}

          {approved && note?.approved_user_name ? (
            <span className="text-muted small ms-2">
              Zatwierdził: {note.approved_user_name}
            </span>
          ) : null}
        </div>

        <Form.Group className="mb-3">
          <Form.Label>Data</Form.Label>
          <Form.Control
            type="date"
            disabled={readOnly}
            isInvalid={!!errors.date}
            value={form.date}
            onChange={(e) => setField("date", e.target.value)}
          />
          <Form.Control.Feedback type="invalid">
            {errors.date}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Start</Form.Label>
          <Form.Control
            type="time"
            step="1"
            disabled={readOnly}
            isInvalid={!!errors.start_time}
            value={form.start_time}
            onChange={(e) => setField("start_time", e.target.value)}
          />
          <Form.Control.Feedback type="invalid">
            {errors.start_time}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Koniec</Form.Label>
          <Form.Control
            type="time"
            step="1"
            disabled={readOnly}
            isInvalid={!!errors.end_time}
            value={form.end_time}
            onChange={(e) => setField("end_time", e.target.value)}
          />
          <Form.Control.Feedback type="invalid">
            {errors.end_time}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Opis</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            disabled={readOnly}
            isInvalid={!!errors.note}
            placeholder="Dlaczego dane wiersza wymagają korekty?"
            value={form.note}
            onChange={(e) => setField("note", e.target.value)}
          />
          <Form.Control.Feedback type="invalid">
            {errors.note}
          </Form.Control.Feedback>
        </Form.Group>

        {readOnly ? (
          <div className="text-muted small">
            {approved
              ? canApproveNote
                ? "Notatka została zatwierdzona i nie można jej już edytować. Aby ją zmienić, usuń ją albo cofnij zatwierdzenie."
                : "Notatka została zatwierdzona i nie można jej już edytować ani usunąć."
              : "Notatkę może dodać tylko pracownik, którego dotyczy wiersz."}
          </div>
        ) : null}

        {error ? (
          <Alert variant="danger" className="mt-3 mb-0">
            {error}
          </Alert>
        ) : null}
      </Modal.Body>

      <Modal.Footer>
        {hasNote && canManageNote && (!approved || canApproveNote) ? (
          <Button
            variant="outline-danger"
            className="me-auto"
            onClick={handleDelete}
            disabled={loading}
          >
            <FontAwesomeIcon
              icon={action === "delete" ? faSpinner : faTrash}
              spin={action === "delete"}
            />{" "}
            Usuń
          </Button>
        ) : null}

        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Zamknij
        </Button>

        {canApproveNote && hasNote ? (
          approved ? (
            <Button
              variant="outline-warning"
              onClick={() => handleApprove(false)}
              disabled={loading}
            >
              <FontAwesomeIcon
                icon={action === "reject" ? faSpinner : faXmark}
                spin={action === "reject"}
              />{" "}
              Cofnij zatwierdzenie
            </Button>
          ) : (
            <Button
              variant="success"
              onClick={() => handleApprove(true)}
              disabled={loading}
            >
              <FontAwesomeIcon
                icon={action === "approve" ? faSpinner : faCheck}
                spin={action === "approve"}
              />{" "}
              Zatwierdź
            </Button>
          )
        ) : null}

        {!readOnly ? (
          <Button variant="primary" onClick={handleSave} disabled={loading}>
            {action === "save" ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin /> Zapisywanie...
              </>
            ) : (
              "Zapisz"
            )}
          </Button>
        ) : null}
      </Modal.Footer>
    </Modal>
  );
}
